import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { prettyDate, prettyFloatMoney } from '@/helpers/utils';
import { DATE_FORMAT_DD_MMM_YYYY } from '@/helpers/common';
import { EmptyContent } from '@/components/EmptyContent';
import { Preloader } from '@/components/Preloader';
import { setModals } from '@/store/actions/commonActions';
import { MODAL_TYPES } from '@/components/ModalProvider';
import { useDispatch } from 'react-redux';
import IconArrow from './chevron-right.svg'
import IconTooltip from '../info-circle.svg'
import s from './index.module.scss';

export const List = ({ data, isLoading, onLoanOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('kit');

  const onTooltipClick = round => {
    dispatch(
      setModals({
        type: MODAL_TYPES.your_earnings,
        round,
      }),
    );
  };

  if (isLoading) {
    return <Preloader className={s.preloader} />;
  }

  if (!isLoading && !data?.total_count) {
    return (
      <div className={s.empty}>
        <EmptyContent
          title={t('loans:no_claims_yet')}
          text={t('loans:your_claims_will_appear_here')}
        />
      </div>
    );
  }

  return (
    <div className={s.wrapper}>
      <div className={s.list}>
        {data.list.map(round => {
          const {
            fundraising_name,
            fundraising_id,
            max_earnings,
            start_date,
            flat_fee_accrued,
            loan_amount,
            repayment_status,
            repaid_amount,
            earnings,
          } = round;

          const showEarnings = fundraising_id > 92 && repayment_status < 100;

          return (
            <div key={fundraising_name} className={s.card}>
              <div className={s.company} onClick={() => onLoanOpen(round)}>
                <div className={s.company_name}>
                  <span className="f-body-accent">{fundraising_name}</span>
                  <img
                    src={IconArrow}
                    alt="arrow"
                    width={18}
                    height={18}
                    style={{ minWidth: '18px' }}
                  />
                </div>
              </div>

              <div className={s.info}>
                <div className={s.row}>
                  <div className="f-small-regular">
                    {t('loan_card.origination_date')}
                  </div>
                  <div className="f-small-accent">
                    {prettyDate(start_date, DATE_FORMAT_DD_MMM_YYYY)}
                  </div>
                </div>

                <div className={s.row}>
                  <div className="f-small-regular">
                    {t('loan_card.loan_amount')}
                  </div>
                  <div className="f-small-accent">
                    {prettyFloatMoney(loan_amount, {
                      decimalPlaces: 2,
                    })}
                  </div>
                </div>

                <div className={s.row}>
                  <div className="f-small-regular">
                    {t('loan_card.repayment_progress')}
                  </div>
                  <div className="f-small-accent">
                    {parseInt(repayment_status)}%
                  </div>
                </div>

                <div className={s.row}>
                  <div className="f-small-regular">
                    {t('loan_card.repayments')}
                  </div>
                  <div className="f-small-accent">
                    {prettyFloatMoney(repaid_amount, {
                      decimalPlaces: 2,
                    })}
                  </div>
                </div>

                <div className={s.row}>
                  <div className="f-small-regular">
                    {t('loan_card.your_earnings')}
                  </div>
                  {showEarnings ? (
                    <div className={classnames(s.earnings_block)}>
                      <div className={s.earnings}>
                        <div className="f-small-accent">
                          +{' '}
                          {prettyFloatMoney(earnings + flat_fee_accrued, {
                            decimalPlaces: 2,
                          })}
                        </div>
                        <div className="f-small-regular">
                          {t('loans:out_of', {
                            amount: prettyFloatMoney(max_earnings, {
                              decimalPlaces: 2,
                            }),
                          })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onTooltipClick(round)}
                      >
                        <img
                          src={IconTooltip}
                          alt="tooltip"
                          width={16}
                          height={16}
                          style={{ minWidth: '16px' }}
                        />
                      </button>
                    </div>
                  ) : (
                    <div className="f-small-accent">
                      +{' '}
                      {prettyFloatMoney(earnings, {
                        decimalPlaces: 2,
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
